/**
 * WHERE THE HOUSEHOLD'S SAVED METHOD STANDS, as ops has it and her newest Stop of it says: one
 * reading, which Payments' saved method card (`screens/Payments.tsx`) and Account's How you pay
 * (`screens/Account.tsx`, slice A1) both draw from, so the two never say different things of the
 * same answer. Ops decides (CLAUDE.md binding rule 1): a method is stopped only once ops's receipt
 * or its push says so, and never because she pressed Stop.
 */
import type { PaymentsPlace, SavedMethod } from '../api.ts';

/**
 * What the saved method's card says, as ops has it: the method with Stop, and a line about her last
 * Stop when it did not go through; the method, sent; the method, with ops's word that her Stop named
 * a method that no longer stands (`changed`, ops's section 11.5); stopped; or nothing saved.
 */
export type Standing =
  | { readonly kind: 'offered'; readonly said?: 'closed' | 'failed' }
  | { readonly kind: 'sent' | 'changed' | 'stopped' | 'nothing_saved' };

/**
 * Which method this is, from when she agreed to it and Stripe's words for it, so a method saved
 * after another, even the same card again, is another.
 */
export const methodKey = (method: SavedMethod): string =>
  JSON.stringify([method.since ?? null, method.label ?? null]);

/**
 * Where the saved method stands. `sentFor` is the method a Stop the door wrote from this page was
 * about (`methodKey`), and `nothingSaved` that the door said nothing was saved to stop: with no
 * method standing, each says why the method is gone, and with neither, nothing is said. With a
 * method standing, her newest Stop as the Worker carries it says where that Stop stands, and one
 * sent from this page about that same method that the Worker does not carry is said to be sent. A
 * method saved after the one she stopped here starts afresh, with Stop.
 */
export function standing(
  place: Pick<PaymentsPlace, 'autopay' | 'stopAsk'>,
  sentFor: string | null,
  nothingSaved: boolean,
): Standing | null {
  if (place.autopay === null) {
    if (nothingSaved) return { kind: 'nothing_saved' };
    return sentFor !== null ? { kind: 'stopped' } : null;
  }
  const ask = place.stopAsk;
  // A Stop sent from this page that the Worker does not carry, as where ops sent no day she agreed.
  if (ask === undefined) {
    return sentFor === methodKey(place.autopay) ? { kind: 'sent' } : { kind: 'offered' };
  }
  switch (ask.state) {
    case 'sending':
    case 'sent':
      return { kind: 'sent' };
    case 'stopped':
      return { kind: 'stopped' };
    case 'not_stopped':
      return { kind: 'nothing_saved' };
    case 'failed':
      return { kind: 'offered', said: 'failed' };
    case 'refused':
      if (ask.code === 'nothing_saved') return { kind: 'nothing_saved' };
      // Her Stop named the method the copy held when she pressed, and ops says another stands
      // (`409 method_changed`, final): nothing was stopped, and a Stop pressed again before ops's
      // push says which method stands would name the same one, so none is offered until then. The
      // Worker gives this Stop only while the copy holds no method agreed after she pressed it.
      if (ask.code === 'method_changed') return { kind: 'changed' };
      return { kind: 'offered', said: ask.code === 'payments_off' ? 'closed' : 'failed' };
  }
}
